<script setup>
import { computed } from 'vue';
import { Head, Link, usePage } from '@inertiajs/vue3';
import FrontLayout from '@/Layouts/FrontLayout.vue';

const props = defineProps({
    setting: Object,
    profil: Object,
    totalKendaraan: [Number, String],
    ulasans: Array,
    title: String,
    meta_description: String,
    meta_keywords: String,
    og_title: String,
    og_description: String,
    og_image: String,
    og_url: String,
    canonical_url: String,
});

const page = usePage();
// Keep page-specific WA logic for the CTA button in the body
const wa = computed(() => props.setting?.setting_no_wa || '6281234567890');
const waUrl = computed(() => `https://wa.me/${wa.value.replace(/^0/, '62')}?text=Halo,%20saya%20ingin%20tanya%20tentang%20Gotransport`);

// Helper for formatted fleet count
const fleetCount = computed(() => {
    return props.totalKendaraan > 0 ? `${props.totalKendaraan}+` : '50+';
});

// Helper formatted stats
const stats = [
    { icon: 'fa fa-car', number: fleetCount.value, label: 'Unit Kendaraan', url: '/daftar-armada', linkText: 'Lihat Armada' }, // Manual link
    { icon: 'fa fa-heart', number: '98%', label: 'Kepuasan Pelanggan' },
    { icon: 'fa fa-calendar', number: '8+', label: 'Tahun Pengalaman' },
];

</script>

<template>
    <FrontLayout 
        :setting="setting" 
        :title="title" 
        :metaDescription="meta_description"
        :og_title="og_title"
        :og_description="og_description"
        :og_image="og_image"
        :og_url="og_url"
        :meta_keywords="meta_keywords"
    >
        <Head>

            <meta name="description" :content="meta_description" />
            <meta v-if="meta_keywords" name="keywords" :content="meta_keywords" />
            <meta property="og:title" :content="og_title" />
            <meta property="og:description" :content="og_description" />
            <meta property="og:image" :content="og_image" />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:type" content="image/jpeg" />
            <meta property="og:url" :content="og_url" />
            <link rel="canonical" :href="canonical_url" />
        </Head>

        <!-- Hero Section (Consistent w/ Ulasan) -->
        <section class="relative overflow-hidden">
             <!-- Background with Orange Gradient -->
             <div class="absolute inset-0 bg-gradient-to-br from-orange-600 via-orange-500 to-amber-400"></div>
             
             <!-- Decorative Elements -->
             <div class="absolute inset-0 overflow-hidden pointer-events-none">
                <svg class="absolute right-0 top-0 text-white/10 w-64 h-64 -mr-16 -mt-16" fill="currentColor" viewBox="0 0 100 100"><circle cx="50" cy="50" r="50"/></svg>
                <div class="absolute left-10 bottom-10 w-20 h-20 bg-white/10 rounded-full blur-xl"></div>
             </div>

             <div class="container mx-auto px-4 lg:px-6 xl:px-8 relative z-10 text-center py-12 md:py-16">
                 <!-- Breadcrumb -->
                 <div class="flex justify-center mb-6">
                     <div class="inline-flex items-center gap-2 text-xs md:text-sm text-white/90 bg-white/10 backdrop-blur-md px-4 py-1.5 rounded-full border border-white/20 shadow-sm">
                        <Link href="/" class="hover:text-white transition-colors font-medium">Beranda</Link>
                        <i class="fa fa-chevron-right text-[10px] opacity-70"></i>
                         <span class="text-white font-bold">Profil</span>
                     </div>
                 </div>

                 <h1 class="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 tracking-tight drop-shadow-sm">{{ profil?.hero_title || 'Tentang Gotransport' }}</h1>
                 <p class="text-amber-100 text-lg max-w-2xl mx-auto font-medium">{{ profil?.hero_subtitle || 'Partner terpercaya untuk kebutuhan transportasi Anda di Banyuwangi' }}</p>
             </div>
             
             <!-- Bottom Wave -->
             <div class="absolute bottom-0 left-0 right-0 text-white">
                 <svg viewBox="0 0 1440 48" fill="currentColor" class="w-full h-auto block">
                     <path d="M0,48 L1440,48 L1440,0 C1440,0 1140,48 720,48 C300,48 0,0 0,0 L0,48 Z"></path>
                 </svg>
             </div>
        </section>

        <!-- Company Overview Section -->
        <section class="py-12 md:py-16 bg-white dark:bg-gray-900 transition-colors duration-300">
            <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl">
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-12">
                    <!-- Left Column: Description & Stats -->
                    <div>
                         <div class="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-sm rounded-2xl p-4 lg:p-4 mb-8 transition-colors">
                             <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-3">
                                 <span class="w-1 h-8 bg-orange-500 rounded-full"></span>
                                 Profil Perusahaan
                             </h2>
                             <div class="prose prose-sm md:prose-base max-w-none text-gray-600 dark:text-gray-300 leading-relaxed dark:prose-invert" v-html="profil?.company_description || 'Gotransport adalah perusahaan rental mobil...'"></div>
                         </div>

                         <!-- Stats -->
                         <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                             <div v-for="(stat, idx) in stats" :key="idx" class="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 text-center border border-gray-100 dark:border-gray-700 hover:bg-orange-50 dark:hover:bg-gray-700 transition-colors group">
                                 <div class="w-10 h-10 mx-auto bg-white dark:bg-gray-700 rounded-full flex items-center justify-center shadow-sm mb-3 group-hover:scale-110 transition-transform text-orange-600 dark:text-orange-400">
                                     <i :class="stat.icon"></i>
                                 </div>
                                 <div class="font-extrabold text-2xl text-gray-900 dark:text-white mb-1">{{ stat.number }}</div>
                                 <div class="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide mb-2">{{ stat.label }}</div>
                                 <Link v-if="stat.url" :href="stat.url" class="text-xs font-bold text-orange-600 dark:text-orange-400 hover:underline">{{ stat.linkText }} &rarr;</Link>
                             </div>
                         </div>
                    </div>

                    <!-- Right Column: Vision & Mission -->
                    <div>
                        <div class="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900 border border-blue-100 dark:border-gray-700 rounded-3xl overflow-hidden shadow-sm transition-colors">
                            <div class="p-4">
                                <!-- Visi -->
                                <div class="flex items-center gap-4 mb-4">
                                    <div class="w-12 h-12 bg-blue-600 text-white rounded-xl flex items-center justify-center shadow-lg shadow-blue-200 dark:shadow-none">
                                        <i class="fa fa-eye text-xl"></i>
                                    </div>
                                    <h3 class="text-xl font-bold text-gray-900 dark:text-white">Visi</h3>
                                </div>
                                <div class="text-gray-700 dark:text-gray-300 leading-relaxed mb-8 pl-16 border-l-2 border-blue-200 dark:border-blue-900" v-html="profil?.vision_text || 'Menjadi perusahaan rental mobil terdepan...'"></div>

                                <!-- Divider -->
                                <hr class="border-blue-200/50 dark:border-gray-700 mb-8 border-dashed">

                                <!-- Misi -->
                                <div class="flex items-center gap-4 mb-4">
                                    <div class="w-12 h-12 bg-indigo-600 text-white rounded-xl flex items-center justify-center shadow-lg shadow-indigo-200 dark:shadow-none">
                                        <i class="fa fa-bullseye text-xl"></i>
                                    </div>
                                    <h3 class="text-xl font-bold text-gray-900 dark:text-white">Misi</h3>
                                </div>
                                <div class="text-gray-700 dark:text-gray-300 leading-relaxed pl-16 border-l-2 border-indigo-200 dark:border-indigo-900" v-html="profil?.mission_text">
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Video Section (if exists) -->
        <section v-if="profil?.video_tour_box" class="py-12 bg-gray-900 text-white">
            <div class="container mx-auto px-4 lg:px-6 max-w-5xl text-center">
                 <h3 class="text-2xl md:text-3xl font-bold mb-3">Video Profil</h3>
                 <p class="text-gray-400 mb-8">Lihat lebih dekat layanan kami</p>
                 <div class="rounded-2xl overflow-hidden shadow-2xl border-4 border-gray-800 bg-black aspect-video">
                     <video controls class="w-full h-full">
                         <source :src="`/storage/videos/${profil.video_tour_box}`" type="video/mp4">
                         Browser Anda tidak mendukung video.
                     </video>
                 </div>
            </div>
        </section>

        <!-- Values Section -->
        <section class="py-12 md:py-16 bg-purple-50 dark:bg-gray-900 border-t border-purple-100 dark:border-gray-800 transition-colors duration-300">
             <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl">
                <div class="text-center mb-10">
                    <span class="text-purple-600 dark:text-purple-400 font-bold tracking-wider text-xs uppercase bg-purple-100 dark:bg-gray-800 px-3 py-1 rounded-full">Values</span>
                    <h3 class="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mt-2">Nilai-Nilai Kami</h3>
                </div>

                <div class="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-4">
                    <div v-for="(val, idx) in (profil?.values_data || [])" :key="idx" class="bg-white dark:bg-gray-800 p-4 rounded-xl border border-purple-100 dark:border-gray-700 hover:border-purple-300 hover:shadow-lg transition-all text-center group">
                        <div class="w-12 h-12 mx-auto bg-purple-100 dark:bg-gray-700 text-purple-600 dark:text-purple-400 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                             <i class="fa fa-check"></i>
                        </div>
                        <h4 class="font-bold text-gray-900 dark:text-white mb-2">{{ val.title }}</h4>
                        <p class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{{ val.description }}</p>
                    </div>
                    <!-- Fallback if empty -->
                    <template v-if="!profil?.values_data?.length">
                        <div class="bg-white dark:bg-gray-800 p-4 rounded-xl border border-purple-100 dark:border-gray-700 text-center">
                            <h4 class="font-bold text-gray-900 dark:text-white">Terpercaya</h4>
                            <p class="text-xs text-gray-500 dark:text-gray-400">Menjaga kepercayaan pelanggan</p>
                        </div>
                         <div class="bg-white dark:bg-gray-800 p-4 rounded-xl border border-purple-100 dark:border-gray-700 text-center">
                            <h4 class="font-bold text-gray-900 dark:text-white">Berkualitas</h4>
                            <p class="text-xs text-gray-500 dark:text-gray-400">Armada terawat & bersih</p>
                        </div>
                         <div class="bg-white dark:bg-gray-800 p-4 rounded-xl border border-purple-100 dark:border-gray-700 text-center">
                            <h4 class="font-bold text-gray-900 dark:text-white">Tepat Waktu</h4>
                            <p class="text-xs text-gray-500 dark:text-gray-400">Menghargai waktu Anda</p>
                        </div>
                         <div class="bg-white dark:bg-gray-800 p-4 rounded-xl border border-purple-100 dark:border-gray-700 text-center">
                            <h4 class="font-bold text-gray-900 dark:text-white">Profesional</h4>
                            <p class="text-xs text-gray-500 dark:text-gray-400">Pelayanan ramah & sopan</p>
                        </div>
                    </template>
                </div>
             </div>
        </section>

        <!-- Testimonials (Simple Horizontal Scroll) -->
        <section class="py-12 md:py-16 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800 transition-colors duration-300">
             <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl">
                <div class="text-center mb-10">
                    <span class="text-yellow-600 dark:text-yellow-400 font-bold tracking-wider text-xs uppercase bg-yellow-100 dark:bg-gray-800 px-3 py-1 rounded-full">Testimoni</span>
                    <h3 class="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mt-2">Ulasan Pelanggan</h3>
                </div>

                <div class="flex gap-4 overflow-x-auto pb-8 snap-x snap-mandatory hide-scrollbar">
                    <div v-for="ulasan in ulasans" :key="ulasan.id" class="min-w-[300px] md:min-w-[350px] snap-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-4 shadow-sm hover:shadow-md transition-all flex flex-col">
                        <div class="flex-1 mb-4">
                             <div class="flex text-yellow-400 text-sm mb-3">
                                 <i class="fa fa-star" v-for="i in ulasan.rating" :key="i"></i>
                             </div>
                             <p class="text-gray-600 dark:text-gray-300 italic text-sm leading-relaxed">"{{ ulasan.pesan }}"</p>
                        </div>
                        <div class="flex items-center gap-3 border-t border-gray-100 dark:border-gray-700 pt-4 mt-auto">
                            <div class="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center text-gray-500 dark:text-gray-300 font-bold text-sm">
                                {{ ulasan.nama.charAt(0) }}
                            </div>
                            <div>
                                <h4 class="font-bold text-gray-900 dark:text-white text-sm">{{ ulasan.nama }}</h4>
                                <p class="text-xs text-gray-500 dark:text-gray-400">{{ ulasan.lokasi || 'Pelanggan' }}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="text-center mt-6">
                    <Link href="/ulasan" class="inline-flex items-center gap-2 px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-xl transition-all shadow-md hover:shadow-lg">
                        <i class="fa fa-comments"></i> Lihat Semua Ulasan
                    </Link>
                </div>
             </div>
        </section>

        <!-- CTA Section -->
        <section class="py-12 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 transition-colors duration-300">
            <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl">
                <div class="max-w-3xl mx-auto text-center">
                    <h3 class="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3 transition-colors">Ingin Berbagi Pengalaman Anda?</h3>
                    <p class="text-gray-600 dark:text-gray-300 mb-6 transition-colors">
                        Kami sangat menghargai feedback dari setiap pelanggan. 
                        Hubungi kami untuk berbagi pengalaman Anda menggunakan layanan Gotransport.
                    </p>
                    <div class="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Link href="/contact" 
                           class="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold transition-colors rounded-full">
                            <i class="fa fa-envelope"></i>
                            <span>Hubungi Kami</span>
                        </Link>
                        <a v-if="setting?.setting_no_wa" :href="waUrl" 
                           target="_blank" rel="noopener" 
                           class="inline-flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-bold transition-colors rounded-full">
                            <i class="fa fa-whatsapp"></i>
                            <span>WhatsApp</span>
                        </a>
                    </div>
                </div>
            </div>
        </section>
    </FrontLayout>
</template>

<style scoped>
.hide-scrollbar::-webkit-scrollbar {
    display: none;
}
.hide-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
}
</style>
